// Globals
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, MouseSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined } from '@ant-design/icons';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

// Containers
import CategoryFiltersFormContainer from '../CategoryFiltersFormContainer/CategoryFiltersFormContainer';

// Helpers
import { useBoolean } from '@/hooks';
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { FilterType } from '@/models/filters.model';
import { ICategoryFilter, ICategoryFilterValue } from '@/models/categories.model';

// Store
import { categoriesSelector, getCategoryDetails, updateCategoryFilters } from '../../feature';
import { useAppDispatch, useAppState } from '@/store';
import { COLORS } from '@/theme';

// Components
import { EditIcon } from '@/icons';
import { Button, Drawer, Select, Spinner, Tag } from '@/ui';
import { InputNumber } from 'antd';
import {
  StyledTabTitleWrapper,
  StyledPageTitle,
  StyledFiltersListWrapper,
  StyledFiltersList,
  StyledFilterButton,
  StyledFormItem,
  StyledFormWrapper,
  StyledForm,
} from '../../components/Styled';
import { HasUnsavedChangesModal, showSuccessToast, useForm } from '@/components';
import CategoriesAPI from '../../../../api/CategoriesAPI';

const sortableFilterId = (filterId: string) => `tab-filter-${filterId}`;

const sortFiltersByOrder = (filters?: ICategoryFilter[]): ICategoryFilter[] => {
  if (!filters?.length) return [];
  return [...filters].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
};

const SortableFilterItem = styled.li<{ $isDragging: boolean }>`
  list-style: none;
  width: 100%;
  min-height: 3.25rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ $isDragging }) => ($isDragging ? COLORS.BLUE_PRIMARY : COLORS.BRIGHT_GRAY)};
  background: ${COLORS.WHITE};
  box-shadow: ${({ $isDragging }) =>
    $isDragging ? '0 8px 24px rgba(0, 0, 0, 0.18)' : '0 1px 3px rgba(0, 0, 0, 0.08)'};
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  overflow: hidden;
`;

const SortableFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  box-sizing: border-box;
`;

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  flex-shrink: 0;
  background: ${COLORS.GRAY_94};
  color: ${COLORS.NORMAL_GRAY};
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

type TabSortableItemProps = {
  filter: ICategoryFilter;
  disabled: boolean;
  onOpen: (filter: ICategoryFilter) => void;
};

const TabSortableItem = ({ filter, disabled, onOpen }: TabSortableItemProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableFilterId(filter.id),
    disabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: disabled ? 0.7 : 1,
  };

  return (
    <SortableFilterItem ref={setNodeRef} style={style} $isDragging={isDragging} {...attributes}>
      <SortableFilterRow>
        <DragHandle ref={setActivatorNodeRef} {...listeners} aria-label="Drag filter order">
          <HolderOutlined />
        </DragHandle>
        <StyledFilterButton type="button" onClick={() => onOpen(filter)} style={{ padding: 0 }}>
          {filter.locale?.uk?.title}
        </StyledFilterButton>
      </SortableFilterRow>
    </SortableFilterItem>
  );
};

const CategoryFiltersTabContainer = () => {
  const { t } = useTranslation(['categories']);
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const canManageFilters = canManage(Resource.FILTER);
  const [form] = useForm();
  const [pivotForm] = useForm();
  const { categoryDetails, isLoadingCategoryFiltersList } = useAppState(categoriesSelector);
  const { value: isOpenModal, setFalse: closeModal, setTrue: openModal } = useBoolean();
  const { value: formOpened, setFalse: closeForm, setTrue: openForm } = useBoolean();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { value: isFilterDrawerOpen, setFalse: closeFilterDrawer, setTrue: openFilterDrawer } = useBoolean();
  const [selectedFilter, setSelectedFilter] = useState<ICategoryFilter | null>(null);
  const [isSavingPivot, setIsSavingPivot] = useState(false);
  const [isDetaching, setIsDetaching] = useState(false);
  const [selectedValues, setSelectedValues] = useState<ICategoryFilterValue[]>([]);
  const [searchOptions, setSearchOptions] = useState<ICategoryFilterValue[]>([]);
  const [selectedValueIds, setSelectedValueIds] = useState<string[]>([]);
  const [newValues, setNewValues] = useState<{ uk: string; ru: string }[]>([]);
  const [isLoadingSelectedValues, setIsLoadingSelectedValues] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isSavingValues, setIsSavingValues] = useState(false);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [isReordering, setIsReordering] = useState(false);
  const [orderedFilters, setOrderedFilters] = useState<ICategoryFilter[]>([]);
  const searchQueryRef = useRef('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const FILTER_VALUES_LIMIT = 20;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() => {
    setOrderedFilters(sortFiltersByOrder(categoryDetails?.filters));
  }, [categoryDetails?.filters]);

  const handleCloseForm = () => {
    if (hasUnsavedChanges) {
      openModal();
      return;
    }

    closeForm();
  };

  const handleCloseModal = () => {
    closeModal();
    closeForm();
    setHasUnsavedChanges(false);
    form.resetFields();
  };

  const handleSubmit = async (filters?: ICategoryFilter[]) => {
    if (!id) return;

    try {
      const selectedFilters = Array.isArray(filters) ? filters : categoryDetails?.filters ?? [];
      const data = {
        data: {
          filters: selectedFilters.map((items) => items.id),
        },
        id,
      };
      await dispatch(updateCategoryFilters(data));
      await dispatch(getCategoryDetails(id));
    } catch {
    } finally {
      closeForm();
    }
  };

  const handleTabFiltersDragEnd = async (event: DragEndEvent) => {
    if (!id || isReordering) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ids = orderedFilters.map((filter) => sortableFilterId(filter.id));
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    const nextOrder = arrayMove(orderedFilters, oldIndex, newIndex);
    setOrderedFilters(nextOrder);

    try {
      setIsReordering(true);
      await dispatch(
        updateCategoryFilters({
          data: { filters: nextOrder.map((filter) => filter.id) },
          id,
        }),
      );
      await dispatch(getCategoryDetails(id));
    } catch {
      setOrderedFilters(sortFiltersByOrder(categoryDetails?.filters));
    } finally {
      setIsReordering(false);
    }
  };

  const handleSaveAndClose = async () => {
    const values = form.getFieldsValue();
    await handleSubmit(values.selectedFilters);
    closeModal();
  };

  const fetchSelectedValues = useCallback(
    async (filterId: string) => {
      if (!id) return;
      try {
        setIsLoadingSelectedValues(true);
        const response = await CategoriesAPI.getCategoryFilterValues(id, filterId, { is_selected: true });
        const values = response.data.data;
        setSelectedValues(values);
        setSelectedValueIds(values.map((v) => v.id));
      } catch {
        setSelectedValues([]);
        setSelectedValueIds([]);
      } finally {
        setIsLoadingSelectedValues(false);
      }
    },
    [id],
  );

  const fetchSearchOptions = useCallback(
    async (filterId: string, title = '', page = 1, append = false) => {
      if (!id) return;
      try {
        setIsLoadingSearch(true);
        const response = await CategoriesAPI.getCategoryFilterValues(id, filterId, {
          title: title || undefined,
          page,
          limit: FILTER_VALUES_LIMIT,
        });
        const values = response.data.data;
        const meta = response.data.meta;
        setSearchOptions((prev) => (append ? [...prev, ...values] : values));
        setSearchPage(page);
        setSearchHasMore(meta.current_page < meta.last_page);
      } catch {
        if (!append) setSearchOptions([]);
      } finally {
        setIsLoadingSearch(false);
      }
    },
    [id],
  );

  const handleOpenFilterDrawer = (filter: ICategoryFilter) => {
    setSelectedFilter(filter);
    openFilterDrawer();
    if (filter.type !== FilterType.SLIDER) {
      fetchSelectedValues(filter.id);
      fetchSearchOptions(filter.id);
    }
  };

  /** pivotForm підключений лише до StyledForm для SLIDER; setFieldsValue лише після монтування форми в Drawer. */
  useEffect(() => {
    if (!isFilterDrawerOpen || !selectedFilter || selectedFilter.type !== FilterType.SLIDER) {
      return;
    }
    pivotForm.setFieldsValue({
      min: selectedFilter.min ?? null,
      max: selectedFilter.max ?? null,
    });
  }, [isFilterDrawerOpen, selectedFilter, pivotForm]);

  const handleCloseFilterDrawer = () => {
    closeFilterDrawer();
    setSelectedFilter(null);
    setSelectedValues([]);
    setSearchOptions([]);
    setSelectedValueIds([]);
    setNewValues([]);
    setSearchPage(1);
    setSearchHasMore(false);
    searchQueryRef.current = '';
    pivotForm.resetFields();
  };

  const handleSavePivotMinMax = async () => {
    if (!id || !selectedFilter) return;

    try {
      setIsSavingPivot(true);
      const values = pivotForm.getFieldsValue() as { min?: number | null; max?: number | null };
      await CategoriesAPI.updateCategoryFilterMinMax(id, selectedFilter.id, {
        min: values.min ?? null,
        max: values.max ?? null,
      });
      await dispatch(getCategoryDetails(id));
    } catch {
    } finally {
      setIsSavingPivot(false);
      handleCloseFilterDrawer();
    }
  };

  const allKnownValues = useMemo(() => {
    const map = new Map<string, ICategoryFilterValue>();
    selectedValues.forEach((v) => map.set(v.id, v));
    searchOptions.forEach((v) => map.set(v.id, v));
    return map;
  }, [selectedValues, searchOptions]);

  const handleValuesChange = (values: string[]) => {
    const existingIds = values.filter((v) => allKnownValues.has(v));
    const newLabels = values.filter((v) => !allKnownValues.has(v));

    setSelectedValueIds(existingIds);
    setNewValues(newLabels.map((label) => ({ uk: label, ru: label })));
  };

  const handleRemoveValue = (valueId: string) => {
    setSelectedValueIds((prev) => prev.filter((selectedId) => selectedId !== valueId));
  };

  const handleRemoveNewValue = (index: number) => {
    setNewValues((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveFilterValues = async () => {
    if (!id || !selectedFilter) return;

    try {
      setIsSavingValues(true);
      await CategoriesAPI.updateCategoryFilterValues(id, selectedFilter.id, {
        value_ids: selectedValueIds,
        new_values: newValues,
      });
      showSuccessToast({ message: t('category_filter_values_save_success', 'Значення збережено') });
      setNewValues([]);
      await fetchSelectedValues(selectedFilter.id);
      await fetchSearchOptions(selectedFilter.id, searchQueryRef.current);
    } catch {
    } finally {
      setIsSavingValues(false);
    }
  };

  const filterValueOptions = useMemo(() => {
    const ids = new Set(selectedValueIds);
    const options = searchOptions
      .filter((v) => !ids.has(v.id))
      .map((v) => ({
        label: v.locale?.uk?.title ?? v.slug,
        value: v.id,
      }));
    const selectedOpts = selectedValueIds.map((valId) => {
      const item = allKnownValues.get(valId);
      return {
        label: item?.locale?.uk?.title ?? item?.slug ?? valId,
        value: valId,
      };
    });
    return [...selectedOpts, ...options];
  }, [searchOptions, selectedValueIds, allKnownValues]);

  const selectedFilterValues = useMemo(
    () => selectedValueIds.map((valId) => allKnownValues.get(valId)).filter((v): v is ICategoryFilterValue => !!v),
    [selectedValueIds, allKnownValues],
  );

  const hasChanges = useMemo(() => {
    const originalIds = selectedValues.map((v) => v.id);
    const sameLength = originalIds.length === selectedValueIds.length;
    const sameIds = sameLength && originalIds.every((sameId) => selectedValueIds.includes(sameId));
    return !sameIds || newValues.length > 0;
  }, [selectedValues, selectedValueIds, newValues]);

  const handleSearch = useCallback(
    (value: string) => {
      if (!selectedFilter) return;
      searchQueryRef.current = value;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchSearchOptions(selectedFilter.id, value);
      }, 300);
    },
    [selectedFilter, fetchSearchOptions],
  );

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!selectedFilter || !searchHasMore || isLoadingSearch) return;
      const target = e.target as HTMLDivElement;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
        fetchSearchOptions(selectedFilter.id, searchQueryRef.current, searchPage + 1, true);
      }
    },
    [selectedFilter, searchHasMore, isLoadingSearch, searchPage, fetchSearchOptions],
  );

  const handleDetachFilter = async () => {
    if (!id || !selectedFilter) return;

    try {
      setIsDetaching(true);
      await CategoriesAPI.detachCategoryFilter(id, selectedFilter.id);
      await dispatch(getCategoryDetails(id));
    } catch {
    } finally {
      setIsDetaching(false);
      handleCloseFilterDrawer();
    }
  };

  const filtersList = useMemo(
    () =>
      orderedFilters.map((filter) => (
        <TabSortableItem
          key={filter.id}
          filter={filter}
          disabled={!canManageFilters || isReordering || isLoadingCategoryFiltersList}
          onOpen={handleOpenFilterDrawer}
        />
      )),
    [orderedFilters, canManageFilters, isReordering, isLoadingCategoryFiltersList, handleOpenFilterDrawer],
  );

  return (
    <>
      <StyledTabTitleWrapper>
        <StyledPageTitle>{t('category_filters_filter')}</StyledPageTitle>
        {canManageFilters && (
          <Button type="primary" icon={<EditIcon />} onClick={openForm}>
            {t('category_filters_edit')}
          </Button>
        )}
      </StyledTabTitleWrapper>
      <StyledFiltersListWrapper>
        <div style={{ width: '100%', maxWidth: '42rem' }}>
          <DndContext
            autoScroll={false}
            collisionDetection={closestCenter}
            sensors={sensors}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleTabFiltersDragEnd}
          >
            <SortableContext
              items={orderedFilters.map((filter) => sortableFilterId(filter.id))}
              strategy={verticalListSortingStrategy}
            >
              <StyledFiltersList>{filtersList}</StyledFiltersList>
            </SortableContext>
          </DndContext>
        </div>
      </StyledFiltersListWrapper>
      <Drawer open={formOpened} onClose={handleCloseForm}>
        {formOpened && (
          <CategoryFiltersFormContainer
            form={form}
            isLoading={isLoadingCategoryFiltersList}
            initialFilters={categoryDetails?.filters}
            onSubmit={handleSubmit}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        )}
      </Drawer>

      <Drawer open={isFilterDrawerOpen} onClose={handleCloseFilterDrawer}>
        {isFilterDrawerOpen && selectedFilter && (
          <StyledFormWrapper style={{ padding: '1.5rem' }}>
            <StyledPageTitle>{selectedFilter?.locale?.uk?.title}</StyledPageTitle>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', width: '100%' }}>
              <div>
                {t('filter_form_type_label')}: {t(`filters_${selectedFilter.type}`)}
              </div>
              {selectedFilter?.locale?.ru?.title && <div>RU: {selectedFilter.locale.ru.title}</div>}
            </div>

            {canManageFilters && selectedFilter.type === FilterType.SLIDER && (
              <StyledForm form={pivotForm} onFinish={handleSavePivotMinMax}>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <StyledFormItem style={{ flex: 1 }} name="min" label={t('filter_form_min_label')}>
                    <InputNumber min={0} />
                  </StyledFormItem>
                  <StyledFormItem style={{ flex: 1 }} name="max" label={t('filter_form_max_label')}>
                    <InputNumber min={0} />
                  </StyledFormItem>
                </div>

                <Button type="primary" htmlType="submit" block loading={isSavingPivot}>
                  {t('save_filter')}
                </Button>
              </StyledForm>
            )}

            {selectedFilter.type !== FilterType.SLIDER && (
              <div style={{ width: '100%' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                  {t('category_filter_values_title', 'Значення фільтра')}
                </div>
                {isLoadingSelectedValues ? (
                  <Spinner />
                ) : (
                  <>
                    {canManageFilters && (
                      <Select
                        showSearch
                        isTags
                        placeholder={t('category_filter_values_search', 'Пошук значень')}
                        value={[...selectedValueIds, ...newValues.map((v) => v.uk)]}
                        onChange={handleValuesChange}
                        onSearch={handleSearch}
                        onPopupScroll={handlePopupScroll}
                        filterOption={false}
                        loading={isLoadingSearch}
                        options={filterValueOptions}
                        maxTagCount={0}
                        maxTagPlaceholder={`${t('category_filter_values_selected', 'Обрано')}: ${
                          selectedValueIds.length + newValues.length
                        }`}
                        style={{ width: '100%' }}
                      />
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                      {selectedFilterValues.map((value) => (
                        <Tag
                          key={value.id}
                          color="gold"
                          closable={canManageFilters}
                          onClose={() => handleRemoveValue(value.id)}
                        >
                          {value.locale?.uk?.title ?? value.slug}
                        </Tag>
                      ))}
                      {newValues.map((value, index) => (
                        <Tag
                          key={`new-${index}`}
                          color="green"
                          closable={canManageFilters}
                          onClose={() => handleRemoveNewValue(index)}
                        >
                          {value.uk} ({t('category_filter_values_new', 'нове')})
                        </Tag>
                      ))}
                    </div>
                    {selectedValueIds.length === 0 && newValues.length === 0 && (
                      <div style={{ marginTop: '0.5rem', color: '#999' }}>
                        {t('category_filter_values_empty', 'Немає значень')}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {canManageFilters && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  marginTop: 'auto',
                  width: '100%',
                  paddingTop: '1rem',
                }}
              >
                {hasChanges && (
                  <Button type="primary" block loading={isSavingValues} onClick={handleSaveFilterValues}>
                    {t('category_filter_values_save', 'Зберегти значення')}
                  </Button>
                )}
                <Button type="primary" danger block loading={isDetaching} onClick={handleDetachFilter}>
                  {t('category_filters_remove_filter', 'Видалити фільтр з категорії')}
                </Button>
              </div>
            )}
          </StyledFormWrapper>
        )}
      </Drawer>
      <HasUnsavedChangesModal
        isOpened={isOpenModal}
        handleCloseModal={handleCloseModal}
        handleSaveAndClose={handleSaveAndClose}
        handleExitAndClose={handleCloseModal}
      />
    </>
  );
};

export default CategoryFiltersTabContainer;
